// Movable, dockable toolbar sections (AutoCAD-classic style). Every toolbar
// button lives — by id — in #toolbar-buttons; here we move each into its section
// and lay the sections out across one or more rows in #toolbar-dock. Sections can
// be dragged by their grip to reorder within a row, move between rows, or start a
// new row (which grows the header and pushes the rest of the app down, since the
// grid's toolbar row is auto-height). The arrangement persists to localStorage.

import { tr } from './i18n';
import {
  type DockDrop,
  type DockRow,
  dropSection,
  wrapRows,
} from './toolbarRows';
import {
  calloutDrop,
  calloutLeaderEnd,
  calloutRows,
} from './toolbarCallouts';

interface Section { id: string; label: string; buttons: string[] }

const SECTIONS: Section[] = [
  { id: 'file', label: 'File', buttons: [
    'btn-open',
    'btn-open-project',
    'btn-save-project',
    'btn-print',
  ] },
  { id: 'history', label: 'History', buttons: [
    'btn-undo',
    'btn-redo',
  ] },
  { id: 'edit', label: 'Edit', buttons: [
    'btn-move',
    'btn-copy',
    'btn-rotate',
    'btn-transform',
    'btn-array-rect',
    'btn-array-polar',
    'btn-delete',
    'btn-pdf-scale',
    'btn-pdf-scale-factor',
  ] },
  { id: 'arrange', label: 'Arrange', buttons: [
    'btn-front',
    'btn-forward',
    'btn-backward',
    'btn-back',
    'btn-flip-h',
    'btn-flip-v',
    'btn-align-left',
    'btn-align-right',
    'btn-align-top',
    'btn-align-bottom',
    'btn-distribute-h',
    'btn-distribute-v',
    'btn-attach-left',
    'btn-attach-right',
    'btn-attach-top',
    'btn-attach-bottom',
  ] },
  { id: 'draw', label: 'Draw', buttons: [
    'btn-edit-polygon',
    'btn-rectangle',
    'btn-polygon',
    'btn-circle',
    'btn-wall',
    'btn-line',
    'btn-arc',
    'btn-connection',
  ] },
  { id: 'annotate', label: 'Annotate', buttons: [
    'btn-dimension',
    'btn-measure',
    'btn-text',
    'btn-leader',
    'btn-revcloud',
  ] },
  { id: 'modify', label: 'Modify', buttons: [
    'btn-offset',
    'btn-trim',
    'btn-fillet',
    'btn-mirror',
    'btn-explode',
    'btn-join',
    'btn-constrain',
  ] },
  { id: 'sauna', label: 'Sauna', buttons: [
    'btn-edit-room',
    'btn-floor-grille',
    'btn-glass',
    'btn-opening',
    'btn-door',
    'btn-inner-wall',
    'btn-bench',
    'btn-bench-support',
    'btn-bench-end',
    'btn-bench-round',
    'btn-heater-wall',
    'btn-backrest',
    'btn-sculpt-bench',
  ] },
  { id: 'flooring', label: 'Flooring', buttons: [
    'btn-floor-tiled',
    'btn-floor-tiled-slope',
    'btn-floor-screed',
    'btn-floor-screed-slope',
  ] },
  { id: 'lighting', label: 'Lighting', buttons: [
    'btn-light-spot',
    'btn-light-sky',
    'btn-light-strip',
    'btn-light-strip-up',
    'btn-light-cove',
    'btn-light-channel',
  ] },
  // How the drawing is looked at: the zoom, the 3D view's camera, and the
  // views drawn from the plan (sections, the ceilings' plans).
  { id: 'views', label: 'Views', buttons: [
    'btn-zoom-out',
    'btn-zoom-in',
    'btn-zoom-fit',
    'btn-zoom-selection',
    'btn-view3d',
    'btn-isolate-3d',
    'btn-isolate-reset-3d',
    'btn-zoom-window',
    'btn-section',
    'btn-section-3d',
    'btn-rcp',
    'btn-ceiling-top',
  ] },
];

// A sheet shows the same toolbar, cut to what works on paper: File and History
// as they are, Delete, Arrange, and the paper versions of Draw / Annotate (the
// sheet's Line / Text / Connect draw like the model's) plus Insert.
const shared = (id: string): Section => SECTIONS.find((s) => s.id === id)!;
const SECTIONS_SCHEMATIC: Section[] = [
  shared('file'),
  shared('history'),
  { id: 'sheet-edit', label: 'Edit', buttons: ['btn-delete'] },
  shared('arrange'),
  { id: 'sheet-draw', label: 'Draw', buttons: [
    'btn-rectangle',
    'btn-polygon',
    'btn-circle',
    'btn-sch-line',
  ] },
  { id: 'schematic', label: 'Annotate', buttons: [
    'btn-dimension',
    'btn-sch-text',
    'btn-leader',
  ] },
  { id: 'sheet-insert', label: 'Insert', buttons: [
    'btn-sch-viewport',
    'btn-sch-conn',
    'btn-sch-image',
  ] },
  // The electrical schematic: placing it, and adding to it by hand.
  { id: 'sheet-elec', label: 'Electrical', buttons: [
    'btn-sch-elec',
    'btn-sch-elec-box',
    'btn-sch-elec-wire',
  ] },
  { id: 'sheet-views', label: 'Views', buttons: [
    'btn-zoom-out',
    'btn-zoom-in',
    'btn-zoom-fit',
    'btn-zoom-window',
  ] },
];
const SHEET_LAYOUT = (): string[][] => [['file', 'history', 'sheet-edit', 'arrange', 'sheet-draw', 'schematic', 'sheet-insert', 'sheet-elec', 'sheet-views']];

/** Which sheet is in view decides which toolbar the dock shows. */
let mode: 'drawing' | 'schematic' = 'drawing';
function activeSections(): Section[] { return mode === 'schematic' ? SECTIONS_SCHEMATIC : SECTIONS; }
function sectionById(id: string): Section | undefined { return activeSections().find((s) => s.id === id); }
function allIds(): string[] { return activeSections().map((s) => s.id); }

/** Default arrangement: every section in one row, wrapped to the window's width. */
function defaultLayout(): string[][] {
  return [['file', 'history', 'edit', 'arrange', 'draw', 'annotate', 'modify', 'sauna', 'flooring', 'lighting', 'views']];
}

const STORE_KEY = 'stenal.toolbarLayout.v2';
let layout: string[][] = defaultLayout();
// The schematic dock's rows (session-only, not persisted).
let schematicLayout: string[][] = SHEET_LAYOUT();
/** The user's own rows for what the dock shows. */
function curLayout(): string[][] {
  if (style === 'tabs') return tabLayout ?? [currentTab().sections];
  return mode === 'schematic' ? schematicLayout : layout;
}
function setCurLayout(L: string[][]): void {
  if (style === 'tabs') { tabLayout = L; return; }
  if (mode === 'schematic') schematicLayout = L; else layout = L;
}
/** Those rows as wrapped to the dock's width by fitRowsToWidth (null = unwrapped).
 *  Derived, never saved: the user's rows are the only thing a drag writes. */
let shown: DockRow[] | null = null;
function shownRows(): DockRow[] { return shown ?? curLayout().map((ids) => ({ ids, soft: false })); }

// ---- The tabbed variant (View ▸ Switch toolbar style) ----
// Instead of every section at once, a strip of tabs — Home / Draw / Annotate /
// Sauna in the model, Sheet and Electrical on a sheet — each showing its own sections on one row.
// Selecting a room brings the Sauna tab forward (PowerPoint's contextual tab).
// The docked arrangement is kept untouched for when the style is switched back.
interface Tab { id: string; label: string; sections: string[] }
const TABS: Tab[] = [
  { id: 'home', label: 'Home', sections: ['file', 'history', 'edit', 'arrange'] },
  { id: 'draw', label: 'Draw', sections: ['draw', 'modify'] },
  { id: 'annotate', label: 'Annotate', sections: ['annotate'] },
  { id: 'sauna', label: 'Sauna', sections: ['sauna', 'flooring', 'lighting'] },
  { id: 'views', label: 'Views', sections: ['views'] },
];
const SHEET_TABS: Tab[] = [
  { id: 'home', label: 'Home', sections: ['file', 'history', 'sheet-edit', 'arrange'] },
  { id: 'sheet', label: 'Sheet', sections: ['sheet-draw', 'schematic', 'sheet-insert'] },
  { id: 'elec', label: 'Electrical', sections: ['sheet-elec'] },
  { id: 'views', label: 'Views', sections: ['sheet-views'] },
];
const STYLE_KEY = 'stenal.toolbarStyle';
let style: 'sections' | 'tabs' = 'sections';
let activeTab = 'home';
/** The active tab's rows as the user dragged them (session-only, reset per tab). */
let tabLayout: string[][] | null = null;
let roomContext = false;
function tabs(): Tab[] { return mode === 'schematic' ? SHEET_TABS : TABS; }
function currentTab(): Tab { return tabs().find((t) => t.id === activeTab) ?? tabs()[0]; }

/** The strip of tabs, as the dock's first row. Divs, not buttons: the dock parks
 *  every <button> it holds back in the hidden host on each render. */
function buildTabStrip(): HTMLElement {
  const strip = document.createElement('div');
  strip.className = 'tb-row tb-tabs';
  for (const t of tabs()) {
    const tab = document.createElement('div');
    tab.className = 'tb-tab' + (t.id === currentTab().id ? ' active' : '');
    tab.dataset.tab = t.id;
    tab.textContent = tr(t.label);
    tab.addEventListener('click', () => showToolbarTab(t.id));
    strip.appendChild(tab);
  }
  return strip;
}

/** Show one tab (tabbed style only). */
export function showToolbarTab(id: string): void {
  if (style !== 'tabs' || !tabs().some((t) => t.id === id)) return;
  activeTab = id;
  tabLayout = null;
  relayout();
}

/** The toolbar style in use. */
export function toolbarStyle(): 'sections' | 'tabs' { return style; }

/** Switch between every section docked at once and the tabbed variant (kept). */
export function setToolbarStyle(next: 'sections' | 'tabs'): void {
  style = next;
  tabLayout = null;
  try { localStorage.setItem(STYLE_KEY, next); } catch { /* storage disabled */ }
  if (next === 'sections') loadLayout();
  relayout();
}

/** Tell the toolbar what is selected: a room brings the Sauna tab forward (once,
 *  when it becomes selected — the user can still pick another tab). */
export function setToolbarContext(ctx: { room: boolean }): void {
  const becameRoom = ctx.room && !roomContext;
  roomContext = ctx.room;
  if (becameRoom && style === 'tabs' && mode === 'drawing' && activeTab !== 'sauna') showToolbarTab('sauna');
}

function loadLayout(): void {
  try {
    const raw = localStorage.getItem(STORE_KEY);
    if (!raw) { layout = defaultLayout(); return; }
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed) && parsed.every((r) => Array.isArray(r))) {
      layout = parsed as string[][];
    }
  } catch { layout = defaultLayout(); }
  reconcileLayout();
}

/** Keep only known section ids (deduped), and append any section missing from a
 *  saved layout (e.g. added in a newer version) so no tools ever disappear. */
function reconcileLayout(): void {
  const seen = new Set<string>();
  let L = curLayout()
    .map((row) => row.filter((id) => !!sectionById(id) && !seen.has(id) && (seen.add(id), true)))
    .filter((row) => row.length > 0);
  const missing = allIds().filter((id) => !seen.has(id));
  if (missing.length) {
    if (L.length) L[L.length - 1].push(...missing);
    else L = [missing];
  }
  if (!L.length) L = mode === 'schematic' ? SHEET_LAYOUT() : defaultLayout();
  setCurLayout(L);
}

function saveLayout(): void {
  if (mode === 'schematic') return; // the schematic dock is fixed / session-only
  try { localStorage.setItem(STORE_KEY, JSON.stringify(layout)); } catch { /* storage full / disabled — layout stays for this session */ }
}

function buttonsHost(): HTMLElement { return document.getElementById('toolbar-buttons')!; }

/** Build a section element and move its buttons (by id) into it. */
function buildSection(sec: Section): HTMLElement {
  const el = document.createElement('div');
  el.className = 'tb-section';
  el.dataset.section = sec.id;
  const grip = document.createElement('div');
  grip.className = 'tb-grip';
  grip.title = tr('Drag to move this toolbar');
  el.appendChild(grip);
  // The buttons, then the section's name under them (Office's group caption), so
  // a row of icons says what it is for.
  const body = document.createElement('div');
  body.className = 'tb-body';
  const buttons = document.createElement('div');
  buttons.className = 'tb-buttons';
  for (const bid of sec.buttons) {
    const btn = document.getElementById(bid);
    if (btn) buttons.appendChild(btn); // reparent — id and handlers are preserved
  }
  const caption = document.createElement('div');
  caption.className = 'tb-caption';
  caption.textContent = tr(sec.label);
  body.append(buttons, caption);
  el.appendChild(body);
  return el;
}

/** (Re)render the dock from the displayed rows. */
function renderDock(): void {
  const dock = document.getElementById('toolbar-dock');
  if (!dock) return;
  hideCallouts();
  // Park any currently-docked buttons back in the hidden host so re-render can
  // freely move them (avoids losing a button if a section is dropped).
  const host = buttonsHost();
  for (const btn of [...dock.querySelectorAll('button')]) host.appendChild(btn);
  dock.innerHTML = '';
  if (style === 'tabs') dock.appendChild(buildTabStrip());
  shownRows().forEach((row, i) => {
    const rowEl = document.createElement('div');
    rowEl.className = 'tb-row';
    rowEl.dataset.row = String(i);
    for (const id of row.ids) {
      const sec = sectionById(id);
      if (sec) rowEl.appendChild(buildSection(sec));
    }
    if (rowEl.childElementCount) dock.appendChild(rowEl);
  });
  refreshSectionVisibility();
}

/** The user's rows changed (or the sections did): render them, then wrap. */
function relayout(): void {
  shown = null;
  renderDock();
  fitRowsToWidth();
}

const ROW_GAP = 2;   // px, matches the .tb-row gap in styles.css
const BRAND_W = 100; // px, matches .tb-row:first-child padding-left (the brand inset)

/** Wrap each of the user's rows that is wider than the dock onto extra rows, so
 *  everything fits the window. The wraps are display only (`shown`): the user's
 *  rows are untouched, so a wider window joins them back up. */
function fitRowsToWidth(): void {
  const dock = document.getElementById('toolbar-dock');
  if (!dock) return;
  const avail = dock.clientWidth;
  if (avail <= 0) return;
  const width = new Map<string, number>();
  for (const el of dock.querySelectorAll<HTMLElement>('.tb-section')) {
    const id = el.dataset.section;
    if (id && el.style.display !== 'none' && !el.classList.contains('tb-empty')) width.set(id, el.offsetWidth);
  }
  // The first row shares its line with the brand (in the tabbed style the tab
  // strip holds that line instead).
  const next = wrapRows(curLayout(), (id) => width.get(id) ?? 0, avail, style === 'tabs' ? avail : avail - BRAND_W, ROW_GAP);
  if (JSON.stringify(next) !== JSON.stringify(shownRows())) {
    shown = next;
    renderDock();
  }
}

/** Hide a section whose buttons are all hidden (e.g. context-only tools), and an
 *  empty row, so the dock doesn't show stray grips. Called after the app updates
 *  per-button visibility. */
export function refreshSectionVisibility(): void {
  const dock = document.getElementById('toolbar-dock');
  if (!dock) return;
  for (const sec of [...dock.querySelectorAll<HTMLElement>('.tb-section')]) {
    const anyVisible = [...sec.querySelectorAll<HTMLElement>('button')].some((b) => b.style.display !== 'none' && !b.classList.contains('tb-off'));
    sec.classList.toggle('tb-empty', !anyVisible);
  }
  for (const row of sectionRows(dock)) {
    const anyVisible = [...row.querySelectorAll<HTMLElement>('.tb-section')].some((s) => !s.classList.contains('tb-empty'));
    row.style.display = anyVisible ? '' : 'none';
  }
}

// ---- Drag to rearrange ----

interface DragState { id: string; ghost: HTMLElement; indicator: HTMLElement; moved: boolean }
let drag: DragState | null = null;

/** The dock's rows of sections (not the tab strip), as rendered. */
function sectionRows(dock: HTMLElement): HTMLElement[] {
  return [...dock.querySelectorAll<HTMLElement>('.tb-row:not(.tb-tabs)')];
}

/** Where the dragged section (`draggedId`) would land for a cursor at (cx, cy),
 *  on the DISPLAYED rows (`DockDrop.at` is a `shownRows()` index). */
function computeDrop(cx: number, cy: number, draggedId: string): DockDrop {
  const dock = document.getElementById('toolbar-dock')!;
  const rows = sectionRows(dock).filter((r) => r.style.display !== 'none');
  const rowEl = rows.find((r) => { const b = r.getBoundingClientRect(); return cy >= b.top && cy <= b.bottom; });
  if (!rowEl) {
    // In a gap / above / below → a new row before the first row below the cursor.
    const below = rows.find((r) => cy <= r.getBoundingClientRect().bottom);
    return { newRow: true, at: below ? Number(below.dataset.row) : shownRows().length, beforeId: null };
  }
  // Within the row: before the first section whose midpoint is right of the cursor.
  const secs = [...rowEl.querySelectorAll<HTMLElement>('.tb-section')]
    .filter((s) => s.dataset.section !== draggedId && !s.classList.contains('tb-empty'));
  const before = secs.find((s) => { const b = s.getBoundingClientRect(); return cx < b.left + b.width / 2; });
  return { newRow: false, at: Number(rowEl.dataset.row), beforeId: before?.dataset.section ?? null };
}

function applyDrop(t: DockDrop, id: string): void {
  setCurLayout(dropSection(shownRows(), id, t));
  reconcileLayout();
  saveLayout();
  relayout();
}

/** Position the insertion indicator for the given drop target. */
function positionIndicator(t: DockDrop, draggedId: string): void {
  const ind = drag!.indicator;
  const dock = document.getElementById('toolbar-dock')!;
  const dr = dock.getBoundingClientRect();
  const rows = sectionRows(dock).filter((r) => r.style.display !== 'none');
  if (t.newRow) {
    // Horizontal bar across the dock at the new-row boundary.
    const below = rows.find((r) => Number(r.dataset.row) >= t.at);
    const y = below ? below.getBoundingClientRect().top : (rows[rows.length - 1]?.getBoundingClientRect().bottom ?? dr.bottom);
    ind.style.left = `${dr.left}px`;
    ind.style.width = `${dr.width}px`;
    ind.style.top = `${y - 1.5}px`;
    ind.style.height = '3px';
    return;
  }
  const rowEl = rows.find((r) => Number(r.dataset.row) === t.at) ?? rows[rows.length - 1];
  const rr = rowEl.getBoundingClientRect();
  let x: number;
  if (t.beforeId) {
    const s = rowEl.querySelector<HTMLElement>(`.tb-section[data-section="${t.beforeId}"]`)!;
    x = s.getBoundingClientRect().left - 2;
  } else {
    const secs = [...rowEl.querySelectorAll<HTMLElement>('.tb-section')].filter((s) => !s.classList.contains('tb-empty') && s.dataset.section !== draggedId);
    x = secs.length ? secs[secs.length - 1].getBoundingClientRect().right + 1 : rr.left;
  }
  ind.style.left = `${x}px`;
  ind.style.width = '3px';
  ind.style.top = `${rr.top}px`;
  ind.style.height = `${rr.height}px`;
}

function onGripDown(e: PointerEvent): void {
  const grip = e.target as HTMLElement;
  const sec = grip.closest<HTMLElement>('.tb-section');
  if (!sec || e.button !== 0) return;
  e.preventDefault();
  hideCallouts();
  const id = sec.dataset.section!;
  const ghost = document.createElement('div');
  ghost.className = 'tb-drag-ghost';
  ghost.textContent = tr(sectionById(id)?.label ?? id);
  ghost.style.left = `${e.clientX + 12}px`;
  ghost.style.top = `${e.clientY + 12}px`;
  const indicator = document.createElement('div');
  indicator.className = 'tb-drop-indicator';
  drag = { id, ghost, indicator, moved: false };
  const startX = e.clientX, startY = e.clientY;
  try { grip.setPointerCapture(e.pointerId); } catch { /* synthetic / capture unavailable — listeners below still track the drag */ }

  const move = (ev: PointerEvent): void => {
    if (!drag) return;
    if (!drag.moved && Math.hypot(ev.clientX - startX, ev.clientY - startY) < 4) return;
    if (!drag.moved) {
      drag.moved = true;
      document.body.appendChild(drag.ghost);
      document.body.appendChild(drag.indicator);
      sec.classList.add('tb-dragging');
    }
    drag.ghost.style.left = `${ev.clientX + 12}px`;
    drag.ghost.style.top = `${ev.clientY + 12}px`;
    positionIndicator(computeDrop(ev.clientX, ev.clientY, id), id);
  };
  const up = (ev: PointerEvent): void => {
    try { grip.releasePointerCapture(ev.pointerId); } catch { /* not captured */ }
    grip.removeEventListener('pointermove', move);
    grip.removeEventListener('pointerup', up);
    grip.removeEventListener('pointercancel', up);
    if (!drag) return;
    const d = drag;
    drag = null;
    d.ghost.remove();
    d.indicator.remove();
    sec.classList.remove('tb-dragging');
    if (d.moved) applyDrop(computeDrop(ev.clientX, ev.clientY, id), id);
  };
  grip.addEventListener('pointermove', move);
  grip.addEventListener('pointerup', up);
  grip.addEventListener('pointercancel', up);
}

// ---- Button names on hover ----
// Resting the pointer on a section shows every button's name in a yellow callout
// below it (toolbarCallouts.ts lays the row out). The overlay takes no pointer
// events, so the toolbar stays clickable through it. View ▸ Show button names on
// hover turns it off; the choice is remembered.

const CALLOUT_KEY = 'stenal.toolbarCallouts';
const CALLOUT_DELAY_MS = 600;
const CALLOUT_EDGE = 6, CALLOUT_GAP = 6;
let calloutsOn = true;
let calloutTimer = 0;
let calloutSection: HTMLElement | null = null;
let calloutPending: HTMLElement | null = null;

/** Whether resting on a toolbar names its buttons. */
export function toolbarCalloutsEnabled(): boolean { return calloutsOn; }

/** Turn the hover callouts on or off (remembered). */
export function setToolbarCallouts(on: boolean): void {
  calloutsOn = on;
  try { localStorage.setItem(CALLOUT_KEY, on ? '1' : '0'); } catch { /* storage disabled */ }
  if (!on) hideCallouts();
}

function hideCallouts(): void {
  if (calloutTimer) { clearTimeout(calloutTimer); calloutTimer = 0; }
  calloutPending = null;
  document.getElementById('tb-callouts')?.remove();
  calloutSection?.classList.remove('tb-called');
  calloutSection = null;
}

/** A button's name: its label, or the head of its tooltip for an icon-only one. */
function buttonName(b: HTMLElement): string {
  const label = b.querySelector('.tb-label')?.textContent?.trim();
  if (label) return label;
  const title = b.dataset.enTitle != null ? tr(b.dataset.enTitle) : (b.getAttribute('title') ?? '');
  return title.split(' — ')[0].replace(/\s*\([^)]*\)\s*$/, '').trim();
}

function showCallouts(sec: HTMLElement): void {
  hideCallouts();
  const buttons = [...sec.querySelectorAll<HTMLButtonElement>('button')].filter((b) => b.offsetParent && b.getBoundingClientRect().width > 0);
  if (!buttons.length) return;
  calloutSection = sec;
  sec.classList.add('tb-called');
  const W = window.innerWidth;
  const overlay = document.createElement('div');
  overlay.id = 'tb-callouts';
  overlay.className = 'tb-callouts';
  const svgNS = 'http://www.w3.org/2000/svg';
  const svg = document.createElementNS(svgNS, 'svg');
  svg.setAttribute('width', String(W));
  svg.setAttribute('height', String(window.innerHeight));
  overlay.appendChild(svg);
  const boxes = buttons.map((b) => {
    const box = document.createElement('div');
    box.className = 'tb-callout' + (b.disabled ? ' disabled' : '');
    box.dataset.for = b.id;
    box.textContent = buttonName(b);
    overlay.appendChild(box);
    return box;
  });
  document.body.appendChild(overlay);
  const at = buttons.map((b) => b.getBoundingClientRect());
  const xs = at.map((r) => r.left + r.width / 2);
  const lay = calloutRows(xs, (maxW) => boxes.map((box) => { box.style.maxWidth = `${maxW}px`; return box.offsetWidth; }), W, CALLOUT_EDGE, CALLOUT_GAP);
  // Rows top-down: the first hangs lower the further its leaders run sideways,
  // so none is too flat to follow; each next one sits just under the last.
  const firstX = xs.map((x, i) => (lay.rowOf[i] === 0 ? lay.lefts[i] + lay.widths[i] / 2 : lay.passes[i][0]));
  const drop = calloutDrop(xs.map((x) => ({ x, w: 0 })), firstX);
  const tops: number[] = [];
  const heights: number[] = [];
  for (let r = 0; r < lay.rows; r++) {
    tops.push(r ? tops[r - 1] + heights[r - 1] + 16 : sec.getBoundingClientRect().bottom + drop);
    heights.push(Math.max(0, ...boxes.filter((_, i) => lay.rowOf[i] === r).map((b) => b.offsetHeight)));
  }
  xs.forEach((x, i) => {
    const r = lay.rowOf[i];
    Object.assign(boxes[i].style, { left: `${lay.lefts[i]}px`, top: `${tops[r]}px` });
    const y0 = at[i].bottom + 1;
    // Down through each row above its own, by the slot kept open for it.
    const pts = [`M${x},${y0}`, ...lay.passes[i].flatMap((px, k) => [`L${px},${tops[k]}`, `L${px},${tops[k] + heights[k]}`]),
      `L${calloutLeaderEnd(lay.lefts[i], lay.widths[i], lay.passes[i].at(-1) ?? x)},${tops[r] + 1}`];
    const leader = document.createElementNS(svgNS, 'path');
    leader.setAttribute('class', 'tb-callout-leader');
    leader.setAttribute('d', pts.join(' '));
    const dot = document.createElementNS(svgNS, 'circle');
    dot.setAttribute('class', 'tb-callout-dot');
    dot.setAttribute('cx', String(x));
    dot.setAttribute('cy', String(y0));
    dot.setAttribute('r', '2.5');
    svg.append(leader, dot);
  });
  overlay.classList.add('shown');
}

/** Rest on a section → its callouts after a pause; moving on to another section
 *  while they show switches at once; leaving the dock hides them. */
function installCallouts(dock: HTMLElement): void {
  try { calloutsOn = localStorage.getItem(CALLOUT_KEY) !== '0'; } catch { /* storage disabled */ }
  dock.addEventListener('pointerover', (e) => {
    const sec = (e.target as HTMLElement).closest<HTMLElement>('.tb-section');
    if (!calloutsOn || drag || !sec || sec === calloutSection || sec === calloutPending) return;
    if (calloutSection) { showCallouts(sec); return; }
    if (calloutTimer) clearTimeout(calloutTimer);
    calloutPending = sec;
    calloutTimer = window.setTimeout(() => {
      calloutTimer = 0;
      calloutPending = null;
      if (sec.isConnected && sec.matches(':hover')) showCallouts(sec);
    }, CALLOUT_DELAY_MS);
  });
  dock.addEventListener('pointerleave', hideCallouts);
  window.addEventListener('blur', hideCallouts);
  window.addEventListener('resize', hideCallouts);
}

/** Restore the default (drawing) toolbar arrangement (View ▸ Reset Toolbars). */
export function resetToolbar(): void {
  layout = defaultLayout();
  try { localStorage.setItem(STORE_KEY, JSON.stringify(layout)); } catch { /* storage disabled */ }
  if (mode === 'drawing' && style === 'sections') relayout();
}

/** Switch the dock between the drawing tools and a schematics sheet's tools
 *  (keeping the shared alignment section). */
export function setToolbarMode(m: 'drawing' | 'schematic'): void {
  if (m === mode) return;
  mode = m;
  tabLayout = null;
  activeTab = currentTab().id;
  relayout();
}

/** Build the dock once at startup and wire the drag handles. */
export function initToolbar(): void {
  window.addEventListener('stenal-uilang', () => relayout()); // re-caption (and re-measure) in the new language
  try { if (localStorage.getItem(STYLE_KEY) === 'tabs') style = 'tabs'; } catch { /* storage disabled */ }
  loadLayout();
  relayout();
  // Re-wrap when the window resizes: a narrower window wraps onto more rows, a
  // wider one joins them back up. Debounced to a frame.
  let raf = 0;
  window.addEventListener('resize', () => {
    if (raf) cancelAnimationFrame(raf);
    raf = requestAnimationFrame(() => {
      raf = 0;
      fitRowsToWidth();
    });
  });
  const dock = document.getElementById('toolbar-dock');
  if (dock) installCallouts(dock);
  // Delegate: any grip's pointerdown starts a drag.
  dock?.addEventListener('pointerdown', (e) => {
    if ((e.target as HTMLElement).classList.contains('tb-grip')) onGripDown(e as PointerEvent);
  });
}
